import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext();
const API_BASE = (process.env.REACT_APP_API_BASE_URL || "http://localhost:5000").replace(/\/+$/, "");

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [adminKycPendingCount, setAdminKycPendingCount] = useState(0);
  const [adminBankPendingCount, setAdminBankPendingCount] = useState(0);

  const getAuthHeaders = useCallback((bearerToken) => ({
    Authorization: `Bearer ${bearerToken || token || localStorage.getItem("token")}`,
  }), [token]);

  const refreshUserProfile = useCallback(async (providedToken) => {
    try {
      const bearer = providedToken || token || localStorage.getItem("token");
      if (!bearer) {
        console.log("[AuthContext] No token found, cannot refresh profile");
        return null;
      }

      const { data } = await axios.get(`${API_BASE}/api/user/profile`, {
        params: { _ts: Date.now() },
        headers: {
          Authorization: `Bearer ${bearer}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (!data?.success || !data?.data) {
        console.warn("[AuthContext] Profile response invalid");
        return JSON.parse(localStorage.getItem("user") || "null");
      }

      const freshUser = {
        ...data.data,
        packageStatus:
          data.data?.packageStatus === "active"
            ? "active"
            : (data.data?.hasActivePackage ? "active" : "inactive"),
      };

      setUser(freshUser);
      localStorage.setItem("user", JSON.stringify(freshUser));
      return freshUser;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 404) {
        console.error("[AuthContext] User session invalid or deleted", error.response?.status);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setToken(null);
        setUser(null);
        return { __tokenInvalid: true };
      }

      console.warn("[AuthContext] API error, keeping existing session:", error.message);
      const existingUser = JSON.parse(localStorage.getItem("user") || "null");
      if (existingUser) {
        setUser(existingUser);
        return existingUser;
      }
      return null;
    }
  }, [token]);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    // ✅ Safety timeout: Never stay in loading state longer than 3 seconds
    // This ensures navigation/UI always becomes interactive
    const safetyTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) console.warn("[AuthContext] Safety timeout triggered, forcing loading=false");
        return false;
      });
    }, 3000);

    if (savedToken) {
      setToken(savedToken);
      refreshUserProfile(savedToken).then((result) => {
        if (result?.__tokenInvalid) {
          console.log("[AuthContext] Token expired, clearing session");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setToken(null);
          setUser(null);
        } else if (!result && savedUser) {
          console.log("[AuthContext] Using cached user data");
          try {
            setUser(JSON.parse(savedUser));
          } catch (_) { }
        }
        clearTimeout(safetyTimeout);
        setLoading(false);
      });
    } else if (savedUser) {
      console.log("[AuthContext] User data without token, clearing");
      localStorage.removeItem("user");
      clearTimeout(safetyTimeout);
      setLoading(false);
    } else {
      clearTimeout(safetyTimeout);
      setLoading(false);
    }

    return () => clearTimeout(safetyTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async ({ emailOrMobile, password, rememberMe = false }) => {
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/login`, { emailOrMobile, password, rememberMe });
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      await refreshUserProfile(data.token);
      await Promise.all([fetchNotificationCount(), fetchAdminKycPendingCount(), fetchAdminBankPendingCount()]);
      return { success: true, role: data.user.role, user: data.user };
    } catch (err) {
      console.error(err);
      return { success: false, message: err.response?.data?.message || "Login failed" };
    }
  };

  const signup = async (formData) => {
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/register`, formData);
      return data;
    } catch (err) {
      throw err.response?.data || { message: "Signup failed" };
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/forgot-password`, { email });
      const success = data?.success !== false;
      return { success, message: data?.message };
    } catch (err) {
      console.error(err);
      return { success: false, message: err.response?.data?.message || "Failed to send OTP" };
    }
  };

  const verifyResetOtp = async (email, otp) => {
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/verify-otp`, { email, otp });
      if (data?.success === false) {
        return { success: false, message: data?.message || "Invalid or expired OTP" };
      }
      return {
        success: true,
        resetToken: data?.resetToken,
        message: data?.message || "OTP verified successfully",
      };
    } catch (err) {
      console.error(err);
      return { success: false, message: err.response?.data?.message || "Invalid or expired OTP" };
    }
  };

  const fetchNotificationCount = useCallback(async () => {
    try {
      const bearer = token || localStorage.getItem("token");
      if (!bearer) return;
      const { data } = await axios.get(`${API_BASE}/api/notifications/unread-count`, {
        headers: getAuthHeaders(bearer),
      });
      setUnreadNotificationsCount(data?.data?.unreadCount || 0);
    } catch (err) {
      console.warn("[AuthContext] Could not fetch notification count", err?.message || err);
    }
  }, [token, getAuthHeaders]);

  const fetchAdminKycPendingCount = useCallback(async () => {
    try {
      const bearer = token || localStorage.getItem("token");
      const currentUser = user || JSON.parse(localStorage.getItem("user") || "null");
      if (!bearer || currentUser?.role !== "admin") {
        setAdminKycPendingCount(0);
        return;
      }

      const { data } = await axios.get(`${API_BASE}/api/admin/kyc/stats/overview`, {
        headers: getAuthHeaders(bearer),
      });
      setAdminKycPendingCount(data?.data?.PENDING || 0);
    } catch (err) {
      console.warn("[AuthContext] Could not fetch pending KYC count", err?.message || err);
    }
  }, [token, user, getAuthHeaders]);

  const fetchAdminBankPendingCount = useCallback(async () => {
    try {
      const bearer = token || localStorage.getItem("token");
      const currentUser = user || JSON.parse(localStorage.getItem("user") || "null");
      if (!bearer || currentUser?.role !== "admin") {
        setAdminBankPendingCount(0);
        return;
      }

      const { data } = await axios.get(`${API_BASE}/api/admin/withdrawals/bank-verifications/stats`, {
        headers: getAuthHeaders(bearer),
      });
      setAdminBankPendingCount(data?.data?.PENDING || 0);
    } catch (err) {
      console.warn("[AuthContext] Could not fetch pending bank count", err?.message || err);
    }
  }, [token, user, getAuthHeaders]);

  const fetchNotifications = useCallback(async () => {
    try {
      const bearer = token || localStorage.getItem("token");
      if (!bearer) return;
      const { data } = await axios.get(`${API_BASE}/api/notifications`, {
        headers: getAuthHeaders(bearer),
      });
      const fetchedNotifications = data?.data || [];
      setNotifications(fetchedNotifications);
      
      // Calculate unread count directly from the fetched list to ensure perfect sync
      const unreadCount = fetchedNotifications.filter(notif => !notif.read).length;
      setUnreadNotificationsCount(unreadCount);
      
      await Promise.all([fetchAdminKycPendingCount(), fetchAdminBankPendingCount()]);
    } catch (err) {
      console.warn("[AuthContext] Could not fetch notifications", err?.message || err);
    }
  }, [token, getAuthHeaders, fetchAdminKycPendingCount, fetchAdminBankPendingCount]);

  const markAllNotificationsRead = async () => {
    try {
      const bearer = token || localStorage.getItem("token");
      if (!bearer) return;
      
      // Optimistically update local state immediately
      setUnreadNotificationsCount(0);
      setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));

      await axios.patch(`${API_BASE}/api/notifications/mark-all-read`, {}, {
        headers: getAuthHeaders(bearer),
      });
      await Promise.all([fetchAdminKycPendingCount(), fetchAdminBankPendingCount()]);
    } catch (err) {
      console.warn("[AuthContext] Could not mark all notifications read", err?.message || err);
    }
  };

  const resetPasswordWithToken = async (resetToken, newPassword) => {
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/auth/reset-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${resetToken}` } }
      );
      const success = data?.success !== false;
      return { success, message: data?.message || "Password reset successfully" };
    } catch (err) {
      console.error(err);
      return { success: false, message: err.response?.data?.message || "Failed to reset password" };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const bearer = token || localStorage.getItem("token");
      if (!bearer) {
        return { success: false, message: "You must be logged in" };
      }
      const { data } = await axios.post(
        `${API_BASE}/api/auth/change-password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${bearer}` } }
      );
      const success = data?.success !== false;
      return { success, message: data?.message || "Password updated successfully" };
    } catch (err) {
      console.error(err);
      return { success: false, message: err.response?.data?.message || "Failed to update password" };
    }
  };

  const logout = () => {
    console.log("[AuthContext] User explicitly logged out");
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  // ✅ Force logout due to invalid/expired token (only called on real auth errors)
  const forceLogoutDueToTokenExpiry = () => {
    console.error("[AuthContext] Forcing logout due to token expiry/invalidity");
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  // ✅ New helper to keep context + localStorage synced
  const updateUser = (newUser) => {
    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
  };

// ✅ Force refresh user profile (for admin changes detection)
const forceRefresh = async () => {
  console.log("[AuthContext] Force refresh triggered");
  await refreshUserProfile();
  await fetchNotifications();
};

// ✅ Set up listeners for real-time admin changes
useEffect(() => {
  if (token) {
    fetchNotifications();
  }

  const intervalId = token
    ? window.setInterval(() => {
      fetchNotifications();
    }, 3000)
    : null;

  // Listen to localStorage changes (storage events from other windows/tabs)
  const handleStorageChange = (e) => {
    if (e.key === "user") {
      console.log("[AuthContext] User data changed in another tab, refreshing...");
      refreshUserProfile();
      fetchNotifications();
    }
  };

  // Listen to custom event for admin changes
  const handleAdminChange = () => {
    console.log("[AuthContext] Admin change event detected, refreshing...");
    refreshUserProfile();
    fetchNotifications();
  };

  // Listen to window focus (user might have opened admin panel)
  const handleWindowFocus = () => {
    console.log("[AuthContext] Window focused, checking for admin changes...");
    refreshUserProfile();
    fetchNotifications();
  };

  window.addEventListener("storage", handleStorageChange);
  window.addEventListener("admin-change", handleAdminChange);
  window.addEventListener("focus", handleWindowFocus);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
    window.removeEventListener("admin-change", handleAdminChange);
    window.removeEventListener("focus", handleWindowFocus);
    if (intervalId) {
      window.clearInterval(intervalId);
    }
  };
}, [token, fetchNotifications, refreshUserProfile]);

return (
  <AuthContext.Provider
    value={{
      user,
      token,
      notifications,
      unreadNotificationsCount,
      adminKycPendingCount,
      adminBankPendingCount,
      login,
      signup,
      logout,
      forceLogoutDueToTokenExpiry,
      updateUser,
      refreshUserProfile,
      forceRefresh,
      requestPasswordReset,
      verifyResetOtp,
      resetPasswordWithToken,
      changePassword,
      fetchNotifications,
      fetchNotificationCount,
      fetchAdminKycPendingCount,
      fetchAdminBankPendingCount,
      markAllNotificationsRead,
      isAuthenticated: !!user && !!token,
      loading,
    }}
  >
    {children}
  </AuthContext.Provider>
);
};

export const useAuth = () => useContext(AuthContext);
